import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';
import Button from '../components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

export default function Checkout() {
  const { cart, subtotal, clearCart } = useCart();
  const [step, setStep] = useState(1); // 1: Shipping, 2: Payment, 3: Success
  const [isProcessing, setIsProcessing] = useState(false);

  const handleNext = () => {
    if (step === 2) {
      setIsProcessing(true);
      setTimeout(() => {
        setIsProcessing(false);
        setStep(3);
        clearCart();
      }, 2000);
    } else {
      setStep(step + 1);
    }
  };

  if (cart.length === 0 && step !== 3) {
    return (
      <div className="min-h-screen pt-40 pb-20 px-4 text-center">
        <h1 className="font-serif text-3xl mb-4">Your bag is empty</h1>
        <Button variant="primary" onClick={() => window.location.hash = '#/shop'}>Return to Shop</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-creamDark pt-32 pb-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-12">
          
          {/* Checkout Form */}
          <div className="flex-grow lg:w-2/3">
            <div className="flex items-center gap-4 mb-10 overflow-x-auto pb-4 scrollbar-hide">
              {[1, 2, 3].map((s) => (
                <div key={s} className={`flex items-center gap-3 whitespace-nowrap ${step >= s ? 'text-navy' : 'text-navy/30'}`}>
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center font-sans text-xs border ${step >= s ? 'bg-navy text-cream border-navy' : 'border-navy/20'}`}>
                    {step > s ? <SafeIcon icon={FiIcons.FiCheck} /> : s}
                  </span>
                  <span className="font-sans text-[10px] uppercase tracking-widest font-bold">
                    {s === 1 ? 'Shipping' : s === 2 ? 'Payment' : 'Complete'}
                  </span>
                  {s < 3 && <div className={`w-12 h-px ${step > s ? 'bg-navy' : 'bg-navy/10'}`} />}
                </div>
              ))}
            </div>

            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div 
                  key="shipping"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="bg-white p-8 md:p-12 border border-brass/10 shadow-sm space-y-8"
                >
                  <h2 className="font-serif text-3xl text-navy">Shipping Details</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="font-sans text-[10px] uppercase tracking-widest text-navy/50 font-bold">First Name</label>
                      <input type="text" className="w-full bg-creamDark/50 border-b border-brass/20 p-4 font-sans text-sm focus:border-saddle outline-none transition-colors" placeholder="John" />
                    </div>
                    <div className="space-y-2">
                      <label className="font-sans text-[10px] uppercase tracking-widest text-navy/50 font-bold">Last Name</label>
                      <input type="text" className="w-full bg-creamDark/50 border-b border-brass/20 p-4 font-sans text-sm focus:border-saddle outline-none transition-colors" placeholder="Doe" />
                    </div>
                    <div className="md:col-span-2 space-y-2">
                      <label className="font-sans text-[10px] uppercase tracking-widest text-navy/50 font-bold">Address</label>
                      <input type="text" className="w-full bg-creamDark/50 border-b border-brass/20 p-4 font-sans text-sm focus:border-saddle outline-none transition-colors" placeholder="123 Workshop Lane" />
                    </div>
                  </div>
                  <Button variant="accent" className="w-full md:w-auto px-12 py-5" onClick={handleNext}>Continue to Payment</Button>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div 
                  key="payment"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="bg-white p-8 md:p-12 border border-brass/10 shadow-sm space-y-8"
                >
                  <h2 className="font-serif text-3xl text-navy">Secure Payment</h2>
                  <div className="space-y-6">
                    <div className="p-4 border border-saddle bg-cream flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <SafeIcon icon={FiIcons.FiCreditCard} className="text-saddle w-6 h-6" />
                        <span className="font-sans text-sm uppercase tracking-widest font-bold">Credit / Debit Card</span>
                      </div>
                      <div className="w-4 h-4 rounded-full border-2 border-saddle flex items-center justify-center">
                        <div className="w-2 h-2 bg-saddle rounded-full" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="font-sans text-[10px] uppercase tracking-widest text-navy/50 font-bold">Card Number</label>
                      <input type="text" className="w-full bg-creamDark/50 border-b border-brass/20 p-4 font-sans text-sm focus:border-saddle outline-none transition-colors" placeholder="0000 0000 0000 0000" />
                    </div>
                  </div>
                  <Button 
                    variant="accent" 
                    className="w-full md:w-auto px-12 py-5" 
                    onClick={handleNext}
                    disabled={isProcessing}
                  >
                    {isProcessing ? 'Processing Transaction...' : `Pay $${subtotal.toFixed(2)}`}
                  </Button>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div 
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white p-12 md:p-20 border border-brass/10 shadow-sm text-center space-y-8"
                >
                  <div className="w-20 h-20 bg-green-50 text-green-600 rounded-full flex items-center justify-center mx-auto mb-8">
                    <SafeIcon icon={FiIcons.FiCheck} className="w-10 h-10" />
                  </div>
                  <h2 className="font-serif text-4xl text-navy">Order Confirmed</h2>
                  <p className="font-sans text-navy/60 max-w-sm mx-auto leading-relaxed">
                    Your artisanal pieces are being prepared in our workshop. You'll receive an email with tracking shortly.
                  </p>
                  <div className="pt-8 border-t border-brass/10">
                    <Button variant="primary" onClick={() => window.location.hash = '#/'}>Return to Home</Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Order Summary */}
          {step < 3 && (
            <div className="w-full lg:w-1/3">
              <div className="bg-navy text-cream p-8 md:p-10 border-t-[6px] border-brass shadow-xl sticky top-40">
                <h3 className="font-serif text-2xl mb-8 border-b border-cream/10 pb-4">Order Summary</h3>
                <div className="space-y-4 mb-8 max-h-60 overflow-y-auto scrollbar-hide">
                  {cart.map(item => (
                    <div key={item.id} className="flex justify-between items-center text-sm">
                      <span className="font-serif italic text-cream/70">{item.name} x{item.quantity}</span>
                      <span className="font-sans font-bold text-brass">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="space-y-3 pt-6 border-t border-cream/10 font-sans">
                  <div className="flex justify-between text-xs tracking-widest uppercase text-cream/50">
                    <span>Shipping</span>
                    <span className="text-brass">Calculated Next</span>
                  </div>
                  <div className="flex justify-between text-2xl font-serif pt-6">
                    <span>Total</span>
                    <span className="text-brass">${subtotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}